import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, GridColumn, Icon, Label, Menu, MenuItem, Popup } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, Pin, getPinColor, useCan, useGetPinOptionsDropdownOptions } from "components";
import { useShowPriceInVat } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, STATUS_IDENTIFIERS } from "models";
import { QuestionResultStatusSelector } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";
import {
  Attachments,
  CustomerCommunicationAnswer,
  GeneralInfo,
  ImportantNotes,
  InterventionActionSelector,
  InterventionChecklists,
  PinCategory,
  Replacements,
  Subitems
} from "modules/AppointmentDetails/components/Interventions/components";
import "modules/AppointmentDetails/components/Interventions/Interventions.scss";
import { canAddSubitems } from "modules/AppointmentDetails/components/Interventions/utils";
import {
  setConnectedInterventionIdToFilter,
  setConnectedQuestionResultIdToFilter,
  useAppointmentDealer,
  useCarBrandsQuery,
  useConnectedQuestionResultIdToFilter,
  useUpdateInterventionMutation
} from "modules/AppointmentDetails/hooks";
import { isCarReadyOrQualityCheck } from "utils/appointmentUtils";
import { getInterventionPriceWithVAT } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { Appointment, CarCheck, Checklist, CommunicationEvent, DealerLocation, Intervention as InterventionModel, PIN_TYPES, Snooze, User } from "models";
import type { Dispatch, MouseEvent, SetStateAction } from "react";

type InterventionItemProps = {
  appointment: Appointment;
  appointmentLocation: DealerLocation;
  intervention: InterventionModel;
  index: number; // TODO: should not use index but id to match
  onRequestCloseTabs: (index: number | null) => void;
  activeIntervention: number | null;
  hasChanges: boolean;
  diagnoseOverviewEvent?: CommunicationEvent;
  setHasChanges: Dispatch<SetStateAction<boolean>>;
  checks?: CarCheck[];
  checklists?: Checklist[];
};

export type InterventionSnoozeData = {
  intervention_id: number;
  note?: string;
  user?: User;
  snooze_history?: Snooze[];
  appointment_id?: number;
  snooze_status_id?: number | null;
  question_result_id?: number | null;
};

export enum INTERVENTION_TABS {
  General = "general",
  Subitems = "subitems",
  PinType = "pintype",
  Attachments = "attachments",
  Checklists = "checklists",
  Replacements = "replacements"
}

//TODO: Refactor this component, separate concerns, tabs, tab data, etc, TabData in the long run should be removed completely, but that's another task
export const Intervention = ({
  appointment,
  appointmentLocation,
  checks,
  intervention,
  index,
  onRequestCloseTabs,
  activeIntervention,
  hasChanges,
  setHasChanges,
  diagnoseOverviewEvent,
  checklists
}: InterventionItemProps) => {
  const { t } = useTranslation();

  const latestPin = intervention.pin_history?.at(0);

  const [showTabs, setShowTabs] = useState(false);
  const [activeTab, setActiveTab] = useState<INTERVENTION_TABS | null>(INTERVENTION_TABS.General);
  const [newActiveTab, setNewActiveTab] = useState<INTERVENTION_TABS | null>(INTERVENTION_TABS.General);
  const [newActiveIntervention, setNewActiveIntervention] = useState<number | null | undefined>();
  const [pinTypeID, setPinTypeID] = useState<PIN_TYPES | null>(latestPin?.pin_type_id ?? null);
  const [showUnsavedChangesConfirmModal, setShowUnsavedChangesConfirmModal] = useState(false);

  const { mutate: updateIntervention } = useUpdateInterventionMutation();
  const showPriceInVat = useShowPriceInVat();
  const appointmentDealer = useAppointmentDealer(appointment);
  const { pinTypeOptions } = useGetPinOptionsDropdownOptions(appointmentDealer);
  const { data: brands = [] } = useCarBrandsQuery();
  const canUpdateInterventions = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.Interventions);
  const connectedQuestionResultId = useConnectedQuestionResultIdToFilter();

  const connectedChecks = checks?.filter(checklist => checklist.intervention_id === intervention.id) || []; // TODO: clean up
  const pendingChecklistsCount = intervention.pending_checklists?.length ?? 0;
  const checksAndPendginChecklistsCount = connectedChecks.length + pendingChecklistsCount;
  const toggleCustomerOkDisabled = intervention.mechanic_fixed || isCarReadyOrQualityCheck(appointment.appointment_status_identifier);
  const hasImportantNotes = Boolean(intervention.pin_history?.some(element => element.visible_important_items));
  const selectedPinTypeOption = pinTypeOptions.find(option => option.value === pinTypeID);
  const attachments = [...(intervention.attachments || []), ...(intervention.diagnose_overview_attachments || [])];

  useEffect(() => {
    return () => setConnectedQuestionResultIdToFilter(null);
  }, []);

  useEffect(() => {
    if (activeTab === INTERVENTION_TABS.Checklists) {
      setConnectedInterventionIdToFilter(intervention.id);
      return;
    }
    setConnectedInterventionIdToFilter(null);
  }, [activeTab, showTabs]);

  useEffect(() => {
    setPinTypeID(latestPin?.pin_type_id ?? null);
  }, [latestPin?.pin_type_id]);

  useEffect(() => {
    if (activeIntervention === index) {
      if (activeTab === null) {
        setActiveTab(INTERVENTION_TABS.General);
      }
      setShowTabs(true);
    } else {
      setActiveTab(null);
      setShowTabs(false);
    }
  }, [activeIntervention]);

  useEffect(() => {
    if (activeTab === INTERVENTION_TABS.Replacements && !intervention.replacements?.length) setActiveTab(INTERVENTION_TABS.General);
  }, [intervention.replacements]);

  const handleTabSelection = (tab: INTERVENTION_TABS) => {
    if (hasChanges) {
      setShowUnsavedChangesConfirmModal(true);
      setNewActiveTab(tab);
    } else {
      setActiveTab(tab);
    }
  };

  const toggleTabs = () => {
    if (hasChanges) {
      setNewActiveIntervention(activeIntervention === index ? null : index);
      setShowUnsavedChangesConfirmModal(true);
      return;
    }

    onRequestCloseTabs(activeIntervention === index ? null : index);
  };

  const handleTab = (tab: INTERVENTION_TABS) => {
    if (hasChanges) {
      setShowUnsavedChangesConfirmModal(true);
      setNewActiveTab(tab);
    } else {
      setActiveTab(tab);
    }
  };

  const handleConfirm = () => {
    setShowUnsavedChangesConfirmModal(false);
    if (newActiveIntervention !== undefined) {
      onRequestCloseTabs(newActiveIntervention);
    }
    setActiveTab(newActiveTab);
    setHasChanges(false);
  };

  const handleSubTab = (evt: React.MouseEvent<HTMLElement>, name: INTERVENTION_TABS) => {
    evt.preventDefault();
    evt.stopPropagation();

    setShowTabs(activeTab === name ? false : true);
    setActiveTab(activeTab === name ? null : name);
    onRequestCloseTabs(index);
  };

  const handlePinType = (pinTypeID: PIN_TYPES | null) => {
    setPinTypeID(pinTypeID);
  };

  const toggleVisibleInPDF = (evt: React.SyntheticEvent) => {
    evt.preventDefault();
    evt.stopPropagation();

    if (!canUpdateInterventions) return;

    if (appointment.appointment_status_identifier !== STATUS_IDENTIFIERS.CanceledStatus) {
      updateIntervention({ ...intervention, visible_to_customer: !intervention.visible_to_customer });
    }
  };

  const handleToggleCustomerOk = (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
    if (!canUpdateInterventions || toggleCustomerOkDisabled) return;

    e.preventDefault();
    e.stopPropagation();

    if (appointment.appointment_status_identifier !== STATUS_IDENTIFIERS.CanceledStatus) {
      updateIntervention({ ...intervention, customer_ok: !intervention.customer_ok });
    }
  };

  const resetConnectedQuestionResultIdOnInterventionClose = () => {
    if ((!showTabs && connectedQuestionResultId !== intervention.question_result_id) || (showTabs && connectedQuestionResultId === intervention.question_result_id))
      setConnectedQuestionResultIdToFilter(null);
  };

  return (
    <div className="Intervention" onClick={resetConnectedQuestionResultIdOnInterventionClose}>
      <Grid className="intervention-main" onClick={toggleTabs}>
        <GridColumn width={10} className="intervention-container">
          <div className="title-container">
            {intervention.check_in_result_id && (
              <div className="question-status-container">
                <div className="view-only">
                  <Icon className="cart icon-container grey" />
                </div>
              </div>
            )}
            {(intervention.check_in_remark_id || intervention.diagnose_overview_remark_id) && (
              <div className="question-status-container">
                <div className="view-only">
                  <Icon className="note sticky icon-container blue" />
                </div>
              </div>
            )}
            {intervention.kiosk_remark_id && (
              <div className="question-status-container">
                <div className="view-only">
                  <Icon className="note sticky icon-container green" />
                </div>
              </div>
            )}
            {intervention.keylocker_remark_id && (
              <div className="question-status-container">
                <div className="view-only">
                  <Icon className="note sticky icon-container purple" />
                </div>
              </div>
            )}
            {intervention.question_result_id && (
              <div
                className={`question-status-container  ${connectedQuestionResultId === intervention.question_result_id ? "active-connected-checklist-item" : ""}`}
                onClick={e => {
                  e.stopPropagation();
                  setConnectedQuestionResultIdToFilter(connectedQuestionResultId === intervention.question_result_id ? null : intervention.question_result_id);
                }}
              >
                <QuestionResultStatusSelector intervention={intervention} disabled />
              </div>
            )}
            <div className="title-container-button">
              {!intervention.is_local && <Icon className="desktop" size="tiny" />}
              <div className="title-container-intervention-text">
                <div className="title-container-intervention-title-container">
                  <p className="ellipsis title-container-intervention-title">{intervention.title}</p>
                </div>
                {intervention.description.trim() && <span className="ellipsis title-container-intervention-description">{intervention.description.trim()}</span>}
              </div>
              <Icon className={`chevron ${showTabs ? "down" : "right"}`} size="small" />
            </div>
          </div>
        </GridColumn>

        <GridColumn width={2} className="intervention-price">
          {appointmentLocation.dms_price_enabled && (
            <p>{`€ ${showPriceInVat ? formatPrice(getInterventionPriceWithVAT(intervention, appointmentLocation)) : formatPrice(intervention.price)}`}</p>
          )}
        </GridColumn>

        <GridColumn width={4} className="intervention-container">
          <div className="column-container float-right">
            <div className="column-container gap">
              {(connectedChecks.length > 0 || pendingChecklistsCount > 0) && (
                <Label className={`checklist-label ${pendingChecklistsCount > 0 ? "red" : ""}`} onClick={e => handleSubTab(e, INTERVENTION_TABS.Checklists)}>
                  <div className="flex-row">
                    <Icon className="standard list" /> {checksAndPendginChecklistsCount}
                  </div>
                </Label>
              )}
              {!!intervention.elements?.length && (
                <Label className="green" onClick={e => handleSubTab(e, INTERVENTION_TABS.Subitems)}>
                  <div className="flex-row">
                    <Icon className="diagram subtask" />
                    {intervention.elements.length}
                  </div>
                </Label>
              )}
              {!!intervention.replacements?.length && (
                <Label className="green" onClick={e => handleSubTab(e, INTERVENTION_TABS.Replacements)}>
                  <div className="flex-row">
                    <Icon className="tire" />
                    {intervention.replacements.length}
                  </div>
                </Label>
              )}

              {attachments.length > 0 && (
                <Label className="skyblue" onClick={e => handleSubTab(e, INTERVENTION_TABS.Attachments)}>
                  <div className="flex-row">
                    <Icon className="paperclip" /> {attachments.length}
                  </div>
                </Label>
              )}
              {latestPin && selectedPinTypeOption && (
                <Pin
                  pin={{ ...latestPin, history: intervention.pin_history ?? [] }} // TODO: add a latestPin: PinWithHistory to intervention in the useQuery select so there is no need to pass a new ref every time
                  location={appointmentLocation}
                  brands={brands}
                  vin={appointment.vin_nr}
                  intervention={intervention}
                  onClick={e => handleSubTab(e, INTERVENTION_TABS.PinType)}
                />
              )}
            </div>
            <CustomerCommunicationAnswer
              interventionIsDeclinedByCustomer={intervention.is_declined_by_customer}
              communicationResultStatus={diagnoseOverviewEvent?.intervention_results?.find(r => r.intervention_id === intervention.id)?.communication_result_status}
            />
            <div className="status-container">
              <Can I="update" the="interventions">
                <div className="action-icon-container" onClick={toggleVisibleInPDF}>
                  <Icon className={`eye ${intervention.visible_to_customer ? "green" : "slash red"} ${canUpdateInterventions ? "pointer" : ""}`} size="small" />
                </div>
              </Can>

              <div className="action-icon-container" onClick={handleToggleCustomerOk}>
                <Icon
                  className={`${intervention.customer_ok ? "check circle green" : "remove circle red"}`}
                  size="large"
                  disabled={!canUpdateInterventions || toggleCustomerOkDisabled}
                />
              </div>

              <div className="pointer position-relative action-icon-container" onClick={e => handleSubTab(e, INTERVENTION_TABS.General)}>
                {intervention.mechanic_fixed && (
                  <>
                    <Popup
                      content={intervention.last_mechanic_name}
                      disabled={!intervention.last_mechanic_name}
                      trigger={
                        <div>
                          <Icon className="wrench green pointer" size="small" />
                          <Icon className="check circle green wrench-check-icon" />
                        </div>
                      }
                    />
                  </>
                )}

                {!intervention.mechanic_fixed && intervention.customer_ok && <Icon className="wrench solid not-fixed-wrench  rotate-270" size="small" />}
                {!intervention.mechanic_fixed && !intervention.customer_ok && <Icon className="wrench regular not-fixed-wrench  rotate-270 grey" size="small" />}
              </div>

              <div className="action-icon-container snooze-intervention-action">
                <InterventionActionSelector intervention={intervention} />
              </div>
            </div>
          </div>
        </GridColumn>

        {!showTabs && hasImportantNotes && (
          <GridColumn width={16} style={{ padding: 0 }}>
            <ImportantNotes data={intervention.pin_history} />
          </GridColumn>
        )}
      </Grid>

      {showTabs && (
        <div className="tabs">
          <Menu tabular>
            <MenuItem name={INTERVENTION_TABS.General} active={activeTab === INTERVENTION_TABS.General} onClick={() => handleTab(INTERVENTION_TABS.General)}>
              <div className={`tab green ${activeTab === "general" ? "active" : ""}`}>
                <Icon className="screwdriver wrench" />
                <p>{t("v8_general_info", "General Info")}</p>
              </div>
            </MenuItem>
            {(!!intervention.elements?.length || canAddSubitems(appointmentLocation, intervention.is_local)) && (
              <MenuItem name={INTERVENTION_TABS.Subitems} active={activeTab === INTERVENTION_TABS.Subitems} onClick={() => handleTab(INTERVENTION_TABS.Subitems)}>
                <div className={`tab darkgreen ${activeTab === INTERVENTION_TABS.Subitems ? "active" : ""}`}>
                  <Icon className="diagram subtask" />
                  <p>
                    {t("v8_subitems", "Subitems")} {!!intervention.elements?.length && `(${intervention.elements.length})`}
                  </p>
                </div>
              </MenuItem>
            )}
            {latestPin && selectedPinTypeOption && (
              <MenuItem name={INTERVENTION_TABS.PinType} active={activeTab === INTERVENTION_TABS.PinType} onClick={() => handleTab(INTERVENTION_TABS.PinType)}>
                <div
                  className={`tab ${getPinColor({ pin: latestPin, vin: appointment.vin_nr, location: appointmentLocation, brands, intervention })} ${activeTab === INTERVENTION_TABS.PinType ? "active" : ""}`}
                >
                  {selectedPinTypeOption.icon}
                  <p>{selectedPinTypeOption.text}</p>
                </div>
              </MenuItem>
            )}
            <MenuItem name={INTERVENTION_TABS.Attachments} active={activeTab === INTERVENTION_TABS.Attachments} onClick={() => handleTab(INTERVENTION_TABS.Attachments)}>
              <div className={`tab skyblue ${activeTab === INTERVENTION_TABS.Attachments ? "active" : ""}`}>
                <Icon className="paperclip" />
                <p>
                  {t("v8_attachments", "Attachments")} {attachments.length > 0 ? `(${attachments.length})` : null}
                </p>
              </div>
            </MenuItem>

            <MenuItem name={INTERVENTION_TABS.Checklists} active={activeTab === INTERVENTION_TABS.Checklists} onClick={() => handleTab(INTERVENTION_TABS.Checklists)}>
              <div
                className={`tab ${pendingChecklistsCount === 0 ? "gray" : pendingChecklistsCount > 0 ? "red" : "green"} ${activeTab === INTERVENTION_TABS.Checklists ? "active" : ""}`}
              >
                <Icon className="standard list" />
                <p>
                  {t("v8_checklists", "Checklists")} {`${checksAndPendginChecklistsCount > 0 ? `(${checksAndPendginChecklistsCount})` : ""}`}
                </p>
              </div>
            </MenuItem>
            {intervention.replacements && intervention.replacements.length > 0 && (
              <MenuItem
                name={INTERVENTION_TABS.Replacements}
                active={activeTab === INTERVENTION_TABS.Replacements}
                onClick={() => handleTab(INTERVENTION_TABS.Replacements)}
              >
                <div className={`tab darkgreen ${activeTab === INTERVENTION_TABS.Replacements ? "active" : ""}`}>
                  <Icon className="tire" />
                  <p>
                    {t("v8_tyre_replacements", "Tyre Replacements")} ({intervention.replacements.length})
                  </p>
                </div>
              </MenuItem>
            )}
          </Menu>
          <div className="tabs-container">
            {activeTab === INTERVENTION_TABS.General && (
              <GeneralInfo
                typeID={pinTypeID}
                intervention={intervention}
                appointment={appointment}
                pinTypeOptions={pinTypeOptions}
                onPinTypeSet={handlePinType}
                onSetHasChanges={setHasChanges}
                handleTabSelection={handleTabSelection}
              />
            )}
            {activeTab === INTERVENTION_TABS.Subitems && <Subitems intervention={intervention} />}
            {selectedPinTypeOption && activeTab === INTERVENTION_TABS.PinType && (
              <PinCategory
                latestPinData={latestPin}
                intervention={intervention}
                selectedPinTypeOption={selectedPinTypeOption}
                appointment={appointment}
                hasChanges={hasChanges}
                setHasChanges={setHasChanges}
              />
            )}
            {activeTab === INTERVENTION_TABS.Attachments && <Attachments intervention={intervention} attachments={attachments} />}
            {activeTab === INTERVENTION_TABS.Checklists && (
              <InterventionChecklists connectedChecks={connectedChecks} intervention={intervention} checklists={checklists} appointment={appointment} />
            )}
            {activeTab === INTERVENTION_TABS.Replacements && <Replacements intervention={intervention} />}
          </div>
        </div>
      )}

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Warning}
        isOpen={showUnsavedChangesConfirmModal}
        handleConfirm={handleConfirm}
        handleCancel={() => setShowUnsavedChangesConfirmModal(false)}
        confirmMsg={t("v8_unsaved_changes_confirm_message", "You have some unsaved changes. If you proceed, these changes will be lost.")}
      />
    </div>
  );
};
